import * as Yup from "yup";
import { useCallback, useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import { useFormik, Form, FormikProvider } from "formik";
// material
import { LoadingButton } from "@mui/lab";
import { styled } from "@mui/material/styles";
import {
  Grid,
  Card,
  Chip,
  Stack,
  Switch,
  Button,
  TextField,
  Typography,
  Autocomplete,
  FormHelperText,
  FormControlLabel,
  Box,
  Snackbar,
  Alert,
} from "@mui/material";
// utils
// import fakeRequest from '../../../utils/fakeRequest';
// @types
import { NewPostFormValues, Post } from "../../types/blog";
//
import { QuillEditor } from "../editor";
import { UploadSingleFile } from "../upload";
import BlogNewPostPreview from "./BlogNewPostPreview";
import {
  generateSlug,
  saveBlogPostToFirebase,
  updateBlogPostInFirebase,
} from "../../utils/firebaseBlogUtils";
import { useLocation, useNavigate, useParams } from "react-router-dom";

// ----------------------------------------------------------------------

const TAGS_OPTION = [
  // Từ khóa ngắn (Short-tail, lượng tìm kiếm cao)
  "máy lạnh",
  "điều hòa",
  "máy lạnh inverter",
  "sửa máy lạnh",
  "lắp đặt máy lạnh",

  // Từ khóa đuôi dài (Long-tail, dễ rank, chuyển đổi cao)
  "máy lạnh giá rẻ Hà Nội",
  "cách sửa máy lạnh không lạnh",
  "máy lạnh Daikin inverter 12000BTU",
  "ưu đãi máy lạnh mùa hè 2025",
  "so sánh máy lạnh Panasonic và LG",
  "máy lạnh treo tường 1 chiều",
  "bảo dưỡng máy lạnh tại nhà TP.HCM",
  "máy lạnh công suất lớn cho nhà xưởng",
  "máy lạnh tiết kiệm điện nhất",
  "lắp đặt điều hòa âm trần giá bao nhiêu",

  // Từ khóa thương mại (Commercial intent, tập trung mua hàng)
  "mua máy lạnh ở đâu uy tín",
  "giá máy lạnh inverter 2025",
  "khuyến mãi điều hòa Daikin",
  "sửa máy lạnh gần đây",

  // Từ khóa mùa vụ & địa phương (Local SEO, mùa hè)
  "máy lạnh chống nóng mùa hè",
  "dịch vụ sửa máy lạnh Hà Nội",
  "máy lạnh giá rẻ TP.HCM",
];

const LabelStyle = styled(Typography)(({ theme }) => ({
  ...theme.typography.subtitle2,
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(1),
}));

// ----------------------------------------------------------------------

export default function BlogNewPostForm() {
  const [open, setOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const [post, setPost] = useState<Post | null>(null);
  const [isEdit, setIsEdit] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [isError, setIsError] = useState(false);
  const navigate = useNavigate();
  const handleOpenPreview = () => {
    setOpen(true);
  };

  const handleClosePreview = () => {
    setOpen(false);
  };

  const NewBlogSchema = Yup.object().shape({
    title: Yup.string().required("Title is required"),
    description: Yup.string().required("Description is required"),
    content: Yup.string().min(100).required("Content is required"),
    cover: Yup.mixed().required("Cover is required"),
  });

  const formik = useFormik<NewPostFormValues>({
    initialValues: {
      title: "",
      slug: "",
      description: "",
      content: "",
      cover: null,
      tags: ["máy lạnh"],
      publish: true,
      comments: true,
      metaTitle: "",
      metaDescription: "",
      metaKeywords: ["máy lạnh"],
    },
    validationSchema: NewBlogSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      if (!values.slug || values.slug === "") {
        values.slug = generateSlug(values.title);
      }
      try {
        if (isEdit && post) {
          // ✏️ UPDATE MODE
          const success = await updateBlogPostInFirebase(post.id, values);

          if (success) {
            enqueueSnackbar("✅ Cập nhật bài viết thành công!", {
              variant: "success",
            });
            setIsError(false);
            localStorage.removeItem(post.id);
            navigate(`/blog/posts`);
          } else {
            enqueueSnackbar("⚠️ Không có thay đổi nào được lưu", {
              variant: "info",
            });
            setIsError(true);
          }

          enqueueSnackbar(`Post "${values.title}" updated successfully!`, {
            variant: "success",
          });
        } else {
          const docId = await saveBlogPostToFirebase(values);
          enqueueSnackbar(`Post created successfully with ID: ${docId}`, {
            variant: "success",
          });
          setIsError(false);
        }

        resetForm();
        handleClosePreview();
      } catch (error) {
        console.error(error);
        enqueueSnackbar("Failed to save post: " + (error as Error).message, {
          variant: "error",
        });
        setIsError(true);
      } finally {
        setSubmitting(false);
        setOpenSnackbar(true);
      }
    },
  });

  const {
    errors,
    values,
    touched,
    handleSubmit,
    isSubmitting,
    setFieldValue,
    getFieldProps,
  } = formik;

  const handleDrop = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (file) {
        setFieldValue("cover", {
          ...file,
          preview: URL.createObjectURL(file),
        });
      }
    },
    [setFieldValue]
  );

  useEffect(() => {
    const postID = queryParams.get("id");

    if (postID) {
      setIsEdit(true);
      const stored = localStorage.getItem(postID);
      const parsed = stored ? JSON.parse(stored) : null;

      if (parsed) {
        setPost(parsed);
        // ✅ Fill dữ liệu vào formik
        formik.setValues({
          title: parsed.title || "",
          slug: parsed.slug || "",
          description: parsed.description || "",
          content: parsed.body || "",
          cover: parsed.cover || null,
          tags: parsed.tags || [],
          publish: true,
          comments: true,
          metaTitle: parsed.title || "",
          metaDescription: parsed.description || "",
          metaKeywords: parsed.tags || [],
        });
      }
    } else {
      setIsEdit(false);
      setPost(null);
    }
  }, []);
  return (
    <>
      <FormikProvider value={formik}>
        <Form noValidate autoComplete="off" onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid size={8}>
              <Card sx={{ p: 3 }}>
                <Stack spacing={3}>
                  <TextField
                    fullWidth
                    label="Post Title"
                    {...getFieldProps("title")}
                    error={Boolean(touched.title && errors.title)}
                    helperText={touched.title && errors.title}
                  />

                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    maxRows={5}
                    label="Description"
                    {...getFieldProps("description")}
                    error={Boolean(touched.description && errors.description)}
                    helperText={touched.description && errors.description}
                  />

                  <div>
                    <LabelStyle>Content</LabelStyle>
                    <QuillEditor
                      id="post-content"
                      value={values.content}
                      onChange={(val) => setFieldValue("content", val)}
                      error={Boolean(touched.content && errors.content)}
                    />
                    {touched.content && errors.content && (
                      <FormHelperText
                        error
                        sx={{ px: 2, textTransform: "capitalize" }}
                      >
                        {touched.content && errors.content}
                      </FormHelperText>
                    )}
                  </div>

                  <div>
                    <LabelStyle>Cover</LabelStyle>
                    <UploadSingleFile
                      maxSize={3145728}
                      accept={{ "image/*": [] }}
                      file={values.cover}
                      onDrop={handleDrop}
                      error={Boolean(touched.cover && errors.cover)}
                    />
                    {touched.cover && errors.cover && (
                      <FormHelperText error sx={{ px: 2 }}>
                        {/* {touched.cover && errors.cover} */}
                      </FormHelperText>
                    )}
                  </div>
                </Stack>
              </Card>
            </Grid>

            <Grid size={4}>
              <Card sx={{ p: 3 }}>
                <Stack spacing={3}>
                  <div>
                    <FormControlLabel
                      control={
                        <Switch
                          {...getFieldProps("publish")}
                          checked={values.publish}
                        />
                      }
                      label="Publish"
                      labelPlacement="start"
                      sx={{
                        mb: 1,
                        mx: 0,
                        width: "100%",
                        justifyContent: "space-between",
                      }}
                    />

                    <FormControlLabel
                      control={
                        <Switch
                          {...getFieldProps("comments")}
                          checked={values.comments}
                        />
                      }
                      label="Enable comments"
                      labelPlacement="start"
                      sx={{
                        mx: 0,
                        width: "100%",
                        justifyContent: "space-between",
                      }}
                    />
                  </div>

                  <Autocomplete
                    multiple
                    freeSolo
                    value={values.tags}
                    onChange={(event, newValue) => {
                      setFieldValue("tags", newValue);
                    }}
                    options={TAGS_OPTION.map((option) => option)}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={option}
                          size="small"
                          label={option}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField {...params} label="Tags" />
                    )}
                  />

                  <TextField
                    fullWidth
                    label="Meta title"
                    {...getFieldProps("metaTitle")}
                  />

                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    maxRows={5}
                    label="Meta description"
                    {...getFieldProps("metaDescription")}
                  />
                  <TextField
                    fullWidth
                    label="Slug"
                    {...getFieldProps("slug")}
                  />
                </Stack>
              </Card>

              <Stack direction="row" justifyContent="flex-end" sx={{ mt: 3 }}>
                <Button
                  fullWidth
                  type="button"
                  color="inherit"
                  variant="outlined"
                  size="large"
                  onClick={handleOpenPreview}
                  sx={{ mr: 1.5 }}
                >
                  Preview
                </Button>
                <LoadingButton
                  fullWidth
                  type="submit"
                  variant="contained"
                  size="large"
                  loading={isSubmitting}
                >
                  {isEdit ? "Update" : "Post"}
                </LoadingButton>
              </Stack>
            </Grid>
          </Grid>
        </Form>
      </FormikProvider>

      <BlogNewPostPreview
        formik={formik}
        isOpenPreview={open}
        onClosePreview={handleClosePreview}
      />
      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity={isError ? "error" : "success"}
          sx={{ width: "100%" }}
        >
          {isError ? "Lưu thất bại" : "Đã lưu thành công!"}
        </Alert>
      </Snackbar>
    </>
  );
}
